import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// mui components
import { Box, Stack } from "@mui/material";

// custom components
import CustomLayout from "../../components/layout";
import LoanProgress from "../../components/loan-progress";
import CreateLoanApplication from "../../components/loan-form/create-loan-application";
import LoanAddressApplication from "../../components/loan-form/loan-address-application";
import CreditReissueApplication from "../../components/loan-form/credit-reissue-application";
import LoanEmploymentApplication from "../../components/loan-form/loan-employment-application";
import LoanAssetsApplication from "../../components/loan-form/loan-assets-application";
import LoanDemographicApplication from "../../components/loan-form/loan-demographic-application";
import EditLoanApplication from "../../components/loan-form/edit-loan-application";
import LoanTermsApplication from "../../components/loan-form/create-loan-terms";
import UnderwritingSummary from "../../components/loan-form/underwriting-summary";
import LoanPricingApplication from "../../components/loan-form/loan-pricing-application";
import LoanFeeApplication from "../../components/loan-form/create-loan-fee";
import LoanDocuments from "../../components/loan-form/create-loan-documents";
import Heading5 from "../../components/typography/heading-05";
import useLoanApplicationNavigation from "../../hooks/useLoanApplicationNavigation";

// mui icons
import { CloseOutlined } from "@mui/icons-material";

// reducer slice
import {
	selectChildOrganizationName,
	selectOrganizationName,
	selectSelectedOrganization,
	selectUserDetails,
} from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";

// custom modal
import ConfirmationModal from "../../components/modal/confirmation-modal";
import DirectPathSelectionModal from "../../components/modal/direct-path-selection-modal";

//helper utils
import { LOAN_CHANNEL_CONSTANTS } from "../../utils/common-constants";
import {
	isCommercialBrokerChannel,
	selectedUserWithLoanChannel,
} from "../../utils/common-helpers";
import { VERBIAGE_CONSTANTS } from "../../utils/verbiage.constants";

// custom styles
import styles from "./index.module.scss";

export const CreateLoan = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const organizationName = useSelector(selectOrganizationName);
	const childOrganizationName = useSelector(selectChildOrganizationName);
	const selectedOrganization = useSelector(selectSelectedOrganization);

	const loanSteps = [
		"Borrower Information",
		"Address",
		"Credit Reissue",
		"Employment and Income",
		"Assets and Liabilities",
		"Declarations and Others",
		"Loan Product and Terms",
		"Underwriting Summary",
		"Loan Pricing",
		`${
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.WHS
				? "Closing Information"
				: "Fees"
		}`,
		"Supporting Documents",
	];

	const [loanApplicationStage, setLoanApplicationStage] = useState(0);
	const [applicationData, setApplicationData] = useState({});
	const [loanGuid, setLoanGuid] = useState("");

	// Commercial Broker submissions choose DirectPath or standard processing on
	// the New Loan screen. Ask again when this page is opened directly so the
	// selection is always captured before the application entry screens.
	const showDirectPath = isCommercialBrokerChannel(
		userDetails,
		impersonatedUser,
		selectedOrganization
	);
	const [directPath, setDirectPath] = useState(location?.state?.directPath);
	const [directPathModalVisible, setDirectPathModalVisible] = useState(false);

	useEffect(() => {
		setDirectPathModalVisible(showDirectPath && directPath === undefined);
	}, [showDirectPath, directPath]);

	const handleConfirmDirectPathModal = (isDirectPath) => {
		setDirectPath(isDirectPath);
		setDirectPathModalVisible(false);
	};

	const handleCloseDirectPathModal = () => {
		setDirectPathModalVisible(false);
		navigate("/new-loan");
	};

	const {
		handleNavigateToStage,
		makeRegisterForStage,
		incompleteSections,
		completedSections,
		progressCurrentStep,
		validateAllSectionsForSubmit,
		markSectionCompletion,
		setCreditProvidedMode,
	} = useLoanApplicationNavigation({
		loanApplicationStage,
		setLoanApplicationStage,
		loanGuid,
	});

	const [confirmModalText, setConfirmModalText] = useState("");
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);

	const handleOpenConfirmModal = () => {
		setConfirmModalVisible(true);
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalVisible(false);
	};

	const handleConfirmExit = () => {
		navigate(`/home`);
	};

	const handleCloseAdminModal = () => {
		navigate(-1);
	};

	const handleSubmitConfirmModal = () => {
		navigate("/admin");
	};

	useEffect(() => {
		// check if admin signed in as another user has an organization
		console.log("150 user roles:", userDetails?.user_roles);
		console.log("150 organization name:", organizationName);

		if (
			userDetails?.user_roles?.includes("admin") &&
			(organizationName === undefined ||
				organizationName === null ||
				organizationName === "")
		) {
			console.log("140 No organization");
			setConfirmModalText(VERBIAGE_CONSTANTS.SIGN_IN_BROKER);
			handleOpenConfirmModal();
		} else {
			console.log("140 organization found:", organizationName);
			handleCloseConfirmModal();
		}
	}, [organizationName, userDetails]);

	const breadcrumbData = [
		{
			label: "Home",
			path: "/home",
		},
		{
			label: "New Loan",
			path: "/pipeline",
		},
	];

	return (
		<Box>
			<CustomLayout breadcrumb={breadcrumbData} disabledOverflow={true}>
				<Stack direction="row" className={styles.stackContainer}>
					<Stack direction="row" className={styles.bodyContainer}>
						<Stack direction="column" className={styles.loanContainer}>
							{/* page title and close btn container */}
							<Box direction="row" className={styles.headerContainer}>
								<Stack className={styles.headerTextContainer}>
									<Heading5
										text={`Step ${
											loanApplicationStage === 0
												? loanApplicationStage + 1
												: loanApplicationStage
										}: ${
											loanSteps[
												loanApplicationStage === 0
													? loanApplicationStage
													: loanApplicationStage - 1
											]
										}`}
										fontType="semibold"
										className={styles.headerText}
									/>
								</Stack>

								<Stack
									className={styles.closeIconContainer}
									onClick={() => {
										setConfirmModalText(VERBIAGE_CONSTANTS.SURE_EXIT);
										handleOpenConfirmModal();
									}}
								>
									<CloseOutlined className={styles.closeIcon} />
								</Stack>
							</Box>

							{/* Keep the existing form hidden until CB brokers finish DirectPath selection. */}
							{(!showDirectPath || typeof directPath === "boolean") &&
								(loanApplicationStage === 0 ? (
								<CreateLoanApplication
									setLoanApplicationStage={setLoanApplicationStage}
									applicationData={applicationData}
									setApplicationData={setApplicationData}
									setLoanGuid={setLoanGuid}
									loanGuid={loanGuid}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
									registerSectionHandler={makeRegisterForStage(0)}
									// need to pass 1 instead of 0 for the component to skip next component
									currentStage={1}
									directPath={directPath}
								/>
							) : loanApplicationStage === 1 ? (
								<EditLoanApplication
									setLoanApplicationStage={setLoanApplicationStage}
									applicationData={applicationData}
									setApplicationData={setApplicationData}
									loanGuid={loanGuid}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
									registerSectionHandler={makeRegisterForStage(1)}
									currentStage={1}
								/>
							) : loanApplicationStage === 2 ? (
								<LoanAddressApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(2)}
									currentStage={2}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
									markSectionCompletion={markSectionCompletion}
									setCreditProvidedMode={setCreditProvidedMode}
								/>
							) : loanApplicationStage === 3 ? (
								<CreditReissueApplication
									setLoanApplicationStage={setLoanApplicationStage}
									setApplicationData={setApplicationData}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(3)}
									currentStage={3}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : loanApplicationStage === 4 ? (
								<LoanEmploymentApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(4)}
									currentStage={4}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : loanApplicationStage === 5 ? (
								<LoanAssetsApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(5)}
									currentStage={5}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : loanApplicationStage === 6 ? (
								<LoanDemographicApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(6)}
									currentStage={6}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : loanApplicationStage === 7 ? (
								<LoanTermsApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(7)}
									currentStage={7}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : loanApplicationStage === 8 ? (
								<UnderwritingSummary
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(8)}
									currentStage={8}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : loanApplicationStage === 9 ? (
								<LoanPricingApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(9)}
									currentStage={9}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
									incompleteSections={incompleteSections}
									loanSteps={loanSteps}
								/>
							) : loanApplicationStage === 10 ? (
								<LoanFeeApplication
									setLoanApplicationStage={setLoanApplicationStage}
									loanGuid={loanGuid}
									registerSectionHandler={makeRegisterForStage(10)}
									currentStage={10}
									organizationName={organizationName}
									childOrganizationName={childOrganizationName}
								/>
							) : (
								loanApplicationStage === 11 && (
									<LoanDocuments
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(11)}
										validateAllSectionsForSubmit={validateAllSectionsForSubmit}
										loanSteps={loanSteps}
										applicationData={applicationData}									
										currentStage={11}
										organizationName={organizationName}
										childOrganizationName={childOrganizationName}
										markSectionCompletion={markSectionCompletion}
										directPath={directPath}
									/>
								)
							))}
						</Stack>

						{/* application progress */}
						{(!showDirectPath || typeof directPath === "boolean") && (
							<LoanProgress
								loanSteps={loanSteps}
								currentStep={progressCurrentStep}
								clickNavigation={Boolean(loanGuid)}
								onNavigateToStage={handleNavigateToStage}
								incompleteSections={incompleteSections}
								completedSections={completedSections}
								loanCreated={Boolean(loanGuid)}
							/>
						)}
					</Stack>
				</Stack>
			</CustomLayout>

			{/* modals */}
			<DirectPathSelectionModal
				open={directPathModalVisible}
				organizationName={selectedOrganization || organizationName}
				handleClose={handleCloseDirectPathModal}
				handleConfirm={handleConfirmDirectPathModal}
			/>
			<ConfirmationModal
				open={confirmModalVisible}
				text={confirmModalText}
				confirmText={
					confirmModalText?.includes("exit") ? "Exit Application" : "Sign In"
				}
				handleConfirm={
					confirmModalText?.includes("exit")
						? handleConfirmExit
						: handleSubmitConfirmModal
				}
				closeText={
					confirmModalText?.includes("exit") ? "Return to Application" : "Close"
				}
				handleClose={
					confirmModalText?.includes("exit")
						? handleCloseConfirmModal
						: handleCloseAdminModal
				}
			/>
		</Box>
	);
};

export default CreateLoan;
